import { ACTIVITY_MAX_SESSIONS_PER_REQUEST, type SessionRepeat } from 'shared';
import {
  DURATION_PRESETS,
  REPEAT_LABELS,
  countOccurrences,
  lastDay,
  type SessionForm,
  type SessionFormErrors,
} from '@/lib/activityTime';
import { FormField } from './ActivityUi';
import type { StaffOption } from './useStaffOptions';

const REPEATS: SessionRepeat[] = ['none', 'weekly', 'biweekly'];

interface SessionFieldsProps {
  /** Prefix for the field ids, so two forms on a page do not collide. */
  idPrefix: string;
  form: SessionForm;
  errors: SessionFormErrors;
  onChange: (form: SessionForm, touched: (keyof SessionForm)[]) => void;
  /** Who can run the session. Omit to leave the leader out of the form. */
  leaders?: { options: StaffOption[]; fallbackName: string };
  /** Whether the session can repeat. A one-off event cannot. */
  allowRepeat: boolean;
  /** Whether it can end on a later day than it starts. Only an open day can. */
  multiDay?: boolean;
}

/**
 * When a session happens: date, times, who runs it and how it repeats. Used
 * where a session is added to an activity and where an activity is created
 * together with its session.
 */
export default function SessionFields({
  idPrefix,
  form,
  errors,
  onChange,
  leaders,
  allowRepeat,
  multiDay = false,
}: SessionFieldsProps) {
  const set = <K extends keyof SessionForm>(key: K, value: SessionForm[K]) =>
    onChange({ ...form, [key]: value }, [key]);

  // The last day follows the first until it is set apart from it, and never
  // falls before it.
  const setFirstDay = (date: string) =>
    onChange({ ...form, date, endDate: form.endDate > date ? form.endDate : '' }, [
      'date',
      'endDate',
      'end',
    ]);
  const setLastDay = (endDate: string) =>
    onChange({ ...form, endDate: endDate === form.date ? '' : endDate }, ['endDate', 'end']);

  const applyPreset = (key: string) => {
    const preset = DURATION_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const { start, end } = preset.apply(form.start);
    onChange({ ...form, start, end }, ['start', 'end']);
  };

  const invalid = (key: keyof SessionForm) =>
    errors[key] ? 'border-destructive focus:ring-destructive/30' : '';

  const occurrences = countOccurrences(form.date, form.repeat, form.repeatUntil);
  const tooMany = occurrences > ACTIVITY_MAX_SESSIONS_PER_REQUEST;
  const leaderMissing =
    !!leaders && !leaders.options.some((s) => s.id === form.leaderUserId);

  return (
    <>
      {multiDay ? (
        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="Primeiro dia"
            htmlFor={`${idPrefix}-date`}
            required
            error={errors.date}
          >
            <input
              id={`${idPrefix}-date`}
              type="date"
              className={`input ${invalid('date')}`}
              value={form.date}
              onChange={(e) => setFirstDay(e.target.value)}
              aria-invalid={!!errors.date}
            />
          </FormField>
          <FormField
            label="Último dia"
            htmlFor={`${idPrefix}-end-date`}
            hint="O mesmo, se for só um dia."
          >
            <input
              id={`${idPrefix}-end-date`}
              type="date"
              className="input"
              value={lastDay(form)}
              min={form.date}
              onChange={(e) => setLastDay(e.target.value)}
            />
          </FormField>
        </div>
      ) : (
        <FormField label="Data" htmlFor={`${idPrefix}-date`} required error={errors.date}>
          <input
            id={`${idPrefix}-date`}
            type="date"
            className={`input ${invalid('date')}`}
            value={form.date}
            onChange={(e) => set('date', e.target.value)}
            aria-invalid={!!errors.date}
          />
        </FormField>
      )}

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Início" htmlFor={`${idPrefix}-start`} required error={errors.start}>
          <input
            id={`${idPrefix}-start`}
            type="time"
            className={`input ${invalid('start')}`}
            value={form.start}
            onChange={(e) => set('start', e.target.value)}
            aria-invalid={!!errors.start}
          />
        </FormField>
        <FormField label="Fim" htmlFor={`${idPrefix}-end`} required error={errors.end}>
          <input
            id={`${idPrefix}-end`}
            type="time"
            className={`input ${invalid('end')}`}
            value={form.end}
            onChange={(e) => set('end', e.target.value)}
            aria-invalid={!!errors.end}
          />
        </FormField>
      </div>

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Duração">
        <span className="text-xs text-muted-foreground">Duração:</span>
        {DURATION_PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => applyPreset(p.key)}
            className="px-2.5 py-1 rounded-md border border-border bg-card text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            {p.label}
          </button>
        ))}
      </div>

      {leaders && (
        <FormField
          label="Dinamizador"
          htmlFor={`${idPrefix}-leader`}
          error={errors.leaderUserId}
          hint="Quem dá esta sessão. Pode ser mudado mais tarde."
        >
          <select
            id={`${idPrefix}-leader`}
            className="input"
            value={form.leaderUserId ?? ''}
            onChange={(e) => set('leaderUserId', e.target.value ? Number(e.target.value) : null)}
          >
            {leaderMissing && form.leaderUserId !== null && (
              <option value={form.leaderUserId}>{leaders.fallbackName}</option>
            )}
            {leaders.options.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </FormField>
      )}

      {allowRepeat && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Repetição" htmlFor={`${idPrefix}-repeat`} error={errors.repeat}>
              <select
                id={`${idPrefix}-repeat`}
                className="input"
                value={form.repeat}
                onChange={(e) => set('repeat', e.target.value as SessionRepeat)}
              >
                {REPEATS.map((r) => (
                  <option key={r} value={r}>
                    {REPEAT_LABELS[r]}
                  </option>
                ))}
              </select>
            </FormField>

            {form.repeat !== 'none' && (
              <FormField
                label="Até"
                htmlFor={`${idPrefix}-repeat-until`}
                required
                error={errors.repeatUntil}
              >
                <input
                  id={`${idPrefix}-repeat-until`}
                  type="date"
                  className={`input ${invalid('repeatUntil')}`}
                  value={form.repeatUntil}
                  min={form.date}
                  onChange={(e) => set('repeatUntil', e.target.value)}
                  aria-invalid={!!errors.repeatUntil}
                />
              </FormField>
            )}
          </div>

          {form.repeat !== 'none' && occurrences > 0 && !errors.repeatUntil && (
            <p
              className={`text-sm ${tooMany ? 'text-destructive' : 'text-muted-foreground'}`}
              aria-live="polite"
            >
              {occurrences === 1
                ? 'Vai ser criada 1 sessão.'
                : `Vão ser criadas ${occurrences} sessões, cada uma com o seu código QR.`}
              {tooMany && ` O máximo é ${ACTIVITY_MAX_SESSIONS_PER_REQUEST}.`}
            </p>
          )}
        </>
      )}
    </>
  );
}
